import { DashboardAndAnalysisFiltersPreview } from '@modules/analysis/components/DashboardAndAnalysisFiltersPreview';
import { WhereClausePreview } from '@modules/common/components/WhereClausePreview';
import { isEmptyWhereClause } from '@modules/common/utils';
import { CompareFieldGroupWrapper } from '@modules/quantify/components/CompareFieldGroupWrapper';
import {
  DEFAULT_COMPARE_FILTER_A_NAME,
  DEFAULT_COMPARE_FILTER_B_NAME,
} from '@modules/quantify/constants';
import {
  useGetQuantifyCompareQuery,
  useGetQuantifyWhereClause,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { Stack, Typography } from '@mui/material';
import { AnalysisUseCase } from 'generated/graphql';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';

export const CompareByFiltersPreview = () => {
  const primaryCompareQuery = useGetQuantifyCompareQuery({
    shouldIncludeDashboardFilters: false,
  });
  const compareFilterAName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.baseQueryName || DEFAULT_COMPARE_FILTER_A_NAME,
  );
  const compareFilterBName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.deltaQueryName || DEFAULT_COMPARE_FILTER_B_NAME,
  );
  const primaryWhereClause = useGetQuantifyWhereClause({
    shouldIncludeDashboardFilters: false,
  });
  const dashboardWhereClause = useQuantifyStore(
    ({ visual }) => visual.dashboardFilters?.whereClause,
  );

  if (!primaryCompareQuery?.whereClause) {
    return null;
  }

  const renderFilterAPreview = () => {
    if (dashboardWhereClause) {
      return (
        <DashboardAndAnalysisFiltersPreview
          dashboardWhereClause={dashboardWhereClause}
          analysisWhereClause={primaryWhereClause}
          analysisUseCase={AnalysisUseCase.Default}
          shouldHideHeader
        />
      );
    }

    const isFilterAPresent =
      !!primaryWhereClause && !isEmptyWhereClause(primaryWhereClause);

    return (
      <>
        {isFilterAPresent ? (
          <WhereClausePreview whereClause={primaryWhereClause} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No {compareFilterAName} filter applied
          </Typography>
        )}
      </>
    );
  };

  const renderFilterBPreview = () => {
    const primaryCompareWhereClause =
      primaryCompareQuery.whereClause ?? EmptyWhereClause;

    if (dashboardWhereClause) {
      return (
        <DashboardAndAnalysisFiltersPreview
          dashboardWhereClause={dashboardWhereClause}
          analysisWhereClause={primaryCompareWhereClause ?? EmptyWhereClause}
          analysisUseCase={AnalysisUseCase.Default}
          shouldHideHeader
        />
      );
    }

    const isFilterBPresent =
      primaryCompareWhereClause &&
      !isEmptyWhereClause(primaryCompareWhereClause);

    return (
      <>
        {isFilterBPresent ? (
          <WhereClausePreview whereClause={primaryCompareWhereClause} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No {compareFilterBName} filter applied
          </Typography>
        )}
      </>
    );
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle1">Compare by Filters</Typography>
        <Typography variant="caption" color="text.secondary">
          (A - B)
        </Typography>
      </Stack>
      <CompareFieldGroupWrapper
        relationIndicatorSx={{
          mt: 0.2,
        }}
        fieldA={
          <Stack spacing={1} pb={1}>
            <Typography variant="body2">{compareFilterAName}</Typography>
            {renderFilterAPreview()}
          </Stack>
        }
        fieldB={
          <Stack spacing={1}>
            <Typography variant="body2">{compareFilterBName}</Typography>
            {renderFilterBPreview()}
          </Stack>
        }
      />
    </Stack>
  );
};
